import * as React from 'react';
import SuperCritical, { SuperCriticalComponent } from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';
import { Organizations, OrganizationViewModel, PCUserViewModel } from './model';
import { PCModal } from 'portcall/pcmodal/view';
import { PCTooltip, MaxLengthTooltip } from 'portcall/pctooltip/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCColumnLayout, PCColumnLayoutTrigger } from 'portcall/pccolumnlayout/view';
import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { InviteUsersView } from 'portcall/inviteusers/view';
import { UserPermissionsView } from 'portcall/userpermissions/view';
import { ActionsMenuButton } from 'portcall/actionsmenubutton/view';

// character limits at which tooltips will be enabled
const MAX_USER_NAME_CHARS = 18,
    MAX_ORG_NAME_CHARS = 20;

export const OrganizationsView = SuperCritical(({ model: m } : { model : Organizations }) =>
    <div className="organizations-page pc-page-fixedwidth">
        {/*<loadingindicator params="request: Request" />*/}
        <ControlBar model={m} />
        <OrgListView model={m} />
        <PageModals model={m} />
    </div>
);

let ControlBar = SuperCritical(({ model: m } : { model : Organizations }) =>
    <div className="org-search pc-actions-links-header">
        <input type="search" className="search-query search-box" placeholder="Search..." autoComplete="off"
            value={m.SearchFilter()}
            onChange={e => m.SearchFilter(e.currentTarget.value)} />
        <ActionsMenuButton onClick={m.ToggleActionsMenu} hidden={!m.Model.CanManageOrgs() && !m.Model.CanEditPermissions()} />
        <ActionsMenu model={m} />
    </div>
);

let ActionsMenu = SuperCritical(({ model: m } : { model : Organizations }) =>
    <div className="pc-actions">
        {m.ShowActionsMenu() &&
            <PCDismissible className="pc-card pc-actions-menu" onDismiss={() => m.ShowActionsMenu(false)}>
                {m.Model.CanManageOrgs() &&
                    <div>
                        <div className="menu-item action-item" onClick={() => m.ShowInviteUsers(true)}>Invite</div>
                        <div className="menu-item-desc pc-action-item-descr">Invite new users or organizations to join {m.Domain.DisplayableUrl()}.</div>
                    </div>
                }
                {m.Model.CanEditPermissions() &&
                    <div>
                        <div className="menu-item action-item" onClick={() => m.ShowUserPermissions(true)}>Edit Permissions</div>
                        <div className="menu-item-desc pc-action-item-descr">Configure what people do within {m.Domain.DisplayableUrl()}.</div>
                    </div>
                }
                {m.Model.CanManageOrgs() &&
                    <div>
                        <div className="menu-item action-item" onClick={() => m.ShowManageOrgs(true)}>Manage Organizations</div>
                        <div className="menu-item-desc pc-action-item-descr">Remove organizations that no longer contribute to {m.Domain.DisplayableUrl()}. Site Admins Only.</div>
                    </div>
                }
            </PCDismissible>
        }
    </div>
);

class OrgListView extends SuperCriticalComponent<{ model : Organizations }> {
    columnLayoutTrigger = new PCColumnLayoutTrigger();

    srender({ model: m } : { model : Organizations }) {
        return (
            <div className="org-list-container">
                <PCColumnLayout className="org-list" gutterPx={10} additionalTrigger={this.columnLayoutTrigger}>
                    {m.Organizations().map(org =>
                        <OrganizationView model={org} key={org.Model.Id()} columnLayoutTrigger={this.columnLayoutTrigger} />
                    )}
                </PCColumnLayout>
            </div>
        );
    }
}

let OrganizationView = SuperCritical(({ model: org, columnLayoutTrigger } : { model : OrganizationViewModel, columnLayoutTrigger : PCColumnLayoutTrigger }) =>
    <div className="pc-card org-card">
        <OrgHeader model={org} />
        <OrgUsersView model={org} columnLayoutTrigger={columnLayoutTrigger} />
    </div>
);

let OrgHeader = SuperCritical(({ model: org } : { model : OrganizationViewModel }) =>
    <div className="pc-card-header">
        <img className="org-thumbnail" src={org.Model.Image.ThumbUrl()} />
        <PCTooltip className="org-name-container pc-text-fixedwidth pc-text-fixedwidth-tooltip"
            shown={() => org.Model.Name().length > MAX_ORG_NAME_CHARS} title={() => org.Model.Name()}>
            <a className="org-name pc-text-smalllabel" href={'#!orgprofile?id=' + org.Model.Id()}>{org.Model.Name() + ' (' + org.Users().length + ')'}</a>
            <div className="my-org">{org.IsMyOrg() ? 'My Organization' : ''}</div>
        </PCTooltip>
    </div>
);

let OrgUsersView = SuperCritical(({ model: org, columnLayoutTrigger } : { model : OrganizationViewModel, columnLayoutTrigger : PCColumnLayoutTrigger }) =>
    <div className="user-list" ref={columnLayoutTrigger.ref /* re-trigger column layout when user list renders */}>
        {org.Users().map(user =>
            <UserView model={user} key={user.Model.Id()} />
        )}
    </div>
);

let UserView = SuperCritical(({ model: user } : { model : PCUserViewModel }) =>
    <div className="user">
        <img className="user-thumbnail" src={user.Model.Image.ThumbUrl()} />
        <PCTooltip className="user-name pc-text-fixedwidth pc-text-fixedwidth-tooltip"
            shown={() => (user.Model.FullName()).length > MAX_USER_NAME_CHARS}
            title={() => user.Model.FullName()}>
            <a href="" className="pc-text-smalltext" onClick={noDefault(user.ToggleProfile)}>
                {user.Model.FullName()}
            </a>
        </PCTooltip>
        <PersonProfilePopupView modelContainer={user.ProfilePopup} />
    </div>
);

let PageModals = SuperCritical(({ model: m } : { model : Organizations }) =>
    <div>
        <UserPermissionsView modelContainer={m.UserPermissions} />
        <InviteUsersView modelContainer={m.InviteUsers} />
        <ManageOrgsModal model={m} />
    </div>
);

let ManageOrgsModal = SuperCritical(({ model: m } : { model : Organizations }) =>
    <PCModal className="manage-orgs-modal" shown={() => m.ShowManageOrgs()} onBackDropClick={() => m?.ShowManageOrgs(false)} /* TH: original HTML template had a tabindex="-1" here */>
        <div className="modal-content">
            <div className="modal-header manageOrgsHeader">
                <button className="close pc-modal-close" onClick={() => m.ShowManageOrgs(false)}>
                    <span>Close</span>
                </button>
                <h2 className="modal-title pc-modal-title">Manage Organizations</h2>
            </div>
            <div className="manage-org-text pc-text-smalllabel">
                Deleting an organization will permanently remove the organization and all of the associated user accounts.
            </div>
            <div className="modal-body manageOrgsBody">
                {m.Organizations().map(org =>
                    <ManageOrgsOrgView model={org} key={org.Model.Id()} />
                )}
            </div>
        </div>
    </PCModal>
);

let ManageOrgsOrgView = SuperCritical(({ model: org } : { model : OrganizationViewModel }) =>
    <div className="modal-org-list-item">
        <img className="org-thumbnail" src={org.Model.Image.ThumbUrl()} />
        <MaxLengthTooltip className="org-name pc-text-fixedwidth pc-text-fixedwidth-tooltip"
            text={() => org.Model.Name()} maxLength={MAX_ORG_NAME_CHARS} />
        <DeleteOrgConfirmation model={org} />
    </div>
);

let DeleteOrgConfirmation = SuperCritical(({ model: org } : { model : OrganizationViewModel }) =>
    <div className="delete">
        {!org.ShowConfirmDelete() ?
            <a href="" onClick={noDefault(() => org.ShowConfirmDelete(true))}>Delete</a>
        :
            <div className="delete-shell">
                <div className="delete-text">
                    Are you sure? All organization members will also be deleted. 
                    <br/><br/>This cannot be undone.<br/>
                </div>
                <div className="action-btns">
                    <button className="close pc-modal-close delete-close" onClick={() => org.ShowConfirmDelete(false)}>Cancel</button>
                    <button className="pc-button" onClick={org.Delete}>Delete</button>
                </div>
            </div>
        }
    </div>
);


// WEBPACK FOOTER //
// ./apps/portcall/organizations/view.tsx